import { useQueryCache } from '@pinia/colada';
import { AxiosError } from 'axios';
import { acceptHMRUpdate, defineStore } from 'pinia';

import { ApplicationWithRenterDto } from '@tallo/applications';
import { useAlertsStore } from '@tallo/design-system';
import { getActivityFeedRootQueryKey } from '@tallo/investor/activity-feed';
import { ApplicationBundleDto, applicationsService, SendApplicationRequestDto } from '@tallo/investor/applications';
import {
  getCompanyWantsToApplyQueryKey,
  getPropertyInquiriesQueryKey,
  getPropertyStatisticsQueryKey,
} from '@tallo/investor/property';
import { renterConversationQueryKey, renterInquiriesQueryKey, renterProfileService } from '@tallo/investor/renter';
import { RenterDto } from '@tallo/renter';

/*
 * TODO: Replace `useRenterProfileStore` with pinia colada queries/mutations.
 */
export const useRenterProfileStore = defineStore({
  id: 'renterProfile',
  state: (): {
    profile: {
      isLoaded: boolean;
      profile: RenterDto | null;
      /** The renter the backend last answered 404 for: one the company never dealt with, or a stale link */
      missingRenterId: string | null;
    };
    bundles: {
      isLoaded: boolean;
      bundleOperationInProgress: boolean;
      bundles: ApplicationBundleDto[];
    };
    applications: {
      isLoaded: boolean;
      selected: ApplicationWithRenterDto[];
    };
  } => ({
    profile: {
      isLoaded: false,
      profile: null,
      missingRenterId: null,
    },
    bundles: {
      isLoaded: false,
      bundleOperationInProgress: false,
      bundles: [],
    },
    applications: {
      isLoaded: false,
      selected: [],
    },
  }),
  getters: {
    getRenterProfile: (state) => state.profile.profile,
    isRenterMissing: (state) => (renterId: string | undefined) =>
      !!renterId && state.profile.missingRenterId === renterId,
    getApplicationBundles: (state) => state.bundles.bundles,
    hasApplicationBundles: (state) => state.bundles.bundles?.length > 0,
    isApplicationSent: (state) => {
      return (propertyId: string) => {
        return state.bundles.bundles.find(({ property }) => property.id === propertyId);
      };
    },
  },
  actions: {
    load(renterId: string) {
      this.loadProfileById(renterId);
      this.loadBundles(renterId);
    },

    async loadProfileById(renterId: string): Promise<void> {
      this.profile.isLoaded = false;

      try {
        this.profile.profile = await renterProfileService.getProfileById(renterId);
        this.profile.isLoaded = true;
      } catch (error) {
        if (!(error instanceof AxiosError && error.response?.status === 404)) {
          throw error;
        }

        // The page says so itself: the backend answers 404 for a renter the company never dealt with
        this.profile.missingRenterId = renterId;
      }
    },

    async loadProfileByEmail(email: string): Promise<void> {
      this.profile.isLoaded = false;
      this.profile.profile = await renterProfileService.getProfileByEmail(email);
      this.profile.isLoaded = true;
    },

    async loadBundles(renterId: string): Promise<void> {
      this.bundles.isLoaded = false;
      this.bundles.bundles = await applicationsService.getBundlesByRenterId(renterId);
      this.bundles.isLoaded = true;
    },

    async loadApplicationsByBundleId(id: string): Promise<void> {
      this.applications.selected = [];
      this.applications.isLoaded = false;

      const applications = await applicationsService.getApplicationsByBundleId(id);
      this.applications.selected = [...applications]
        .filter((application) => !!application)
        .sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());

      this.applications.isLoaded = true;
    },

    async sendApplication(
      inquiryId: string,
      requestData?: SendApplicationRequestDto,
      loadBundles = true,
    ): Promise<void> {
      const bundle = await applicationsService.send(inquiryId, requestData);

      // The bundle comes from the response itself rather than from the refetch below — it is what
      // hides the "Send application" button and reveals the Applications card, and neither should
      // hang on a second round trip (the new-lead flow never even makes one).
      this.putBundle(bundle);
      this.invalidateActivityFeed();
      this.invalidateConversation();
      this.invalidateInquiries(bundle.property.id);

      if (loadBundles && this.profile.profile?.id) {
        await this.loadBundles(this.profile.profile.id);
      }
    },

    async resendApplication(applicationBundleId: string): Promise<void> {
      await applicationsService.resend(applicationBundleId);
      await this.loadBundles(this.profile.profile?.id!);
    },

    /** Every action here writes an activity_feed row, so the feed on screen is stale until refetched. */
    invalidateActivityFeed(): void {
      useQueryCache().invalidateQueries({ key: getActivityFeedRootQueryKey() });
    },

    /**
     * Sending the application (or asking for more info on it) writes a message into the renter's
     * thread AND a trace on it, so an already-fetched transcript is stale — the same reason the
     * showing mutations invalidate this key.
     */
    invalidateConversation(): void {
      useQueryCache().invalidateQueries({ key: [renterConversationQueryKey] });
    },

    /**
     * Sending, accepting and declining an application all move the inquiry's rent stage on the BE,
     * so the stage pill on the inquiry card and the leads list keep reading the old stage until
     * these refetch. The renter key is invalidated by prefix — one renter profile is open at a
     * time — while the property keys need the id, which the bundle carries. A sent application also
     * takes the renter off the Wants to apply lists: the unit's sits under its inquiries key, Home's doesn't.
     */
    invalidateInquiries(propertyId: string): void {
      const queryCache = useQueryCache();

      queryCache.invalidateQueries({ key: [renterInquiriesQueryKey] });
      queryCache.invalidateQueries({ key: getPropertyInquiriesQueryKey(propertyId) });
      queryCache.invalidateQueries({ key: getCompanyWantsToApplyQueryKey() });
      // `applicationsCompleted` on the property dashboard counts the same rows.
      queryCache.invalidateQueries({ key: getPropertyStatisticsQueryKey(propertyId) });
    },

    async acceptBundle(applicationBundleId: string): Promise<void> {
      const alerts = useAlertsStore();

      this.bundles.bundleOperationInProgress = true;

      try {
        const acceptedBundle = await applicationsService.accept(applicationBundleId);
        this.putBundle(acceptedBundle);
        this.invalidateActivityFeed();
        this.invalidateInquiries(acceptedBundle.property.id);
      } catch (error) {
        alerts.somethingWentWrong();
      } finally {
        this.bundles.bundleOperationInProgress = false;
      }
    },

    async declineBundle(applicationBundleId: string): Promise<void> {
      const alerts = useAlertsStore();

      this.bundles.bundleOperationInProgress = true;

      try {
        const declinedBundle = await applicationsService.decline(applicationBundleId);
        this.putBundle(declinedBundle);
        this.invalidateActivityFeed();
        this.invalidateInquiries(declinedBundle.property.id);
      } catch (error) {
        alerts.somethingWentWrong();
      } finally {
        this.bundles.bundleOperationInProgress = false;
      }
    },

    /**
     * Rethrows on purpose, unlike accept/decline: the BE Fair-Housing-screens the message and
     * answers 406 with the reason, which the modal renders inline instead of a generic toast.
     */
    async requestBundleUpdate(applicationBundleId: string, message: string): Promise<void> {
      this.bundles.bundleOperationInProgress = true;

      try {
        const updatedBundle = await applicationsService.requestUpdate(applicationBundleId, message);
        this.putBundle(updatedBundle);
        this.invalidateActivityFeed();
        this.invalidateConversation();
      } finally {
        this.bundles.bundleOperationInProgress = false;
      }
    },

    putBundle(bundle: ApplicationBundleDto): void {
      const index = this.bundles.bundles.findIndex(({ id }) => id === bundle.id);

      if (index > -1) {
        this.bundles.bundles[index] = bundle;
      } else {
        this.bundles.bundles.push(bundle);
      }
    },

    async approveCriminalScreening(applicationId: string): Promise<boolean> {
      const alerts = useAlertsStore();

      try {
        const updated = await applicationsService.approveCriminalScreening(applicationId);
        const index = this.applications.selected.findIndex((a) => a.id === applicationId);
        if (index > -1) {
          this.applications.selected[index] = {
            ...this.applications.selected[index],
            criminalScreeningApprovedAt: updated.criminalScreeningApprovedAt,
          };
        }
        return true;
      } catch {
        alerts.somethingWentWrong();
        return false;
      }
    },
  },
});

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useRenterProfileStore, import.meta.hot));
}
